import React, { Fragment, useContext } from "react"
import { AccountBalanceWallet } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { Alert } from "react-bootstrap";

import Indent from "./Indent"
import { Spaces } from "./Spaces"
import SkeletonText from "./SkeletonText"
import useLog from "../hooks/useLog"
import { FormattedNumber } from "./FormattedNumber"
import { TextCopyQR } from "./WithPopup/TextCopyQR"
import { UserContext } from "../Contexts/UserContext/UserContext"
import { TextDisplay, TextDisplayToken } from "./TextDisplay/TextDisplay"
import Constants, { appConfig } from "../config.js";


// TODO: use this method on all slides
/**
 * props:
 * 	 - tokenid		(optional) force a tokenid instead of using the selected one from userContext
 * 	 - noicon		remove wallet icon
 * 	 - row			compact single-row list item (no wallet icon, no "balance" label,
 * 	  				keeps token gradient background + logo watermark) — used to list all tokens
 *   - address		(optional, str) to display a receiving address (ignored in `row` mode)
 */
export default function SoldeToken(props) {
	const function_id = "SoldeToken(): "
	const log = useLog(function_id)

	const userContext = useContext(UserContext)
	let coinProps

	if (props.tokenid) {
		coinProps = userContext.get_coin_props(props.tokenid)
	} else {
		coinProps = userContext.get_selected_coin_props()
	}

	const accent = coinProps.color || appConfig.theme.primaryColor

	const balance_token = userContext.tokens_balance[coinProps.tokenid]
	log("coinProps:", coinProps)
	log("balance_token=", balance_token, "for coinProps.tokenid=", coinProps.tokenid)

	// ---- balance block ---------------------------------------------------
	var jsx_balance = <SkeletonText width="6em" height="1.9em" />

	if (balance_token != null && balance_token !== "") {
		jsx_balance = (
			<div style={{
				display: "flex",
				alignItems: "baseline",
				justifyContent: "center",
				gap: "0.45em",
				margin: "0.35em 0 0.2em",
				color: accent,
				fontWeight: 600,
			}}>
				<span style={{ fontSize: "1.9em", letterSpacing: "0.5px" }}>
					<FormattedNumber value={balance_token} decimals="2" separator=" " />
				</span>
				<span style={{
					fontSize: "1.05em",
					fontWeight: 700,
					letterSpacing: "1px",
					opacity: 0.85,
				}}>
					{coinProps.ticker}
				</span>
			</div>
		)
	}

	// ---- header label -----------------------------------------------------
	var jsx_icon = props.noicon ? null : (
		<Fragment>
			<AccountBalanceWallet />
		</Fragment>
	)

	const jsx_header = (
		<div style={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: "0.5em",
			color: appConfig.theme.textColor2,
			fontSize: "0.95em",
			letterSpacing: "0.5px",
			textTransform: "uppercase",
		}}>
			{jsx_icon}
			<span>{coinProps.name} balance</span>
		</div>
	)

	// ---- receive address card --------------------------------------------
	var jsx_address = null
	if (props.address) {
		jsx_address = (
			<div style={{
				marginTop: "1.4em",
				backgroundColor: "rgba(0,0,0,0.22)",
				border: "1px solid rgba(255,255,255,0.06)",
				borderRadius: "0.7em",
				padding: "1.1em 1.2em",
			}}>
				<div style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: "0.55em",
					color: "white",
					fontWeight: 600,
					fontSize: "1em",
					marginBottom: "0.85em",
					letterSpacing: "0.3px",
				}}>
					<i className="fas fa-hand-holding-usd" />
					<span>Receive</span>
				</div>

				{userContext.isMockWallet ?
					<Alert variant="light" style={{ margin: "0", padding: "0.55em", fontSize: "15px" }}>
						<i style={{ fontSize: "25px", marginRight: "10px" }} className="fa fa-info-circle" />
						{Constants.MOCK_WALLET_PHRASE}
					</Alert>
					:
					<div style={{
						backgroundColor: "rgba(255,255,255,0.04)",
						borderRadius: "0.5em",
						padding: "0.8em 0.9em",
						fontFamily: "monospace",
						fontSize: "14px",
						wordBreak: "break-all",
						textAlign: "center",
					}}>
						<TextCopyQR value={props.address} />
					</div>
				}
			</div>
		)
	}

	// ---- row render (compact list item) ----------------------------------
	// slim single-row entry for listing tokens: no wallet icon, no "balance"
	// label, but keeps the token gradient background + right-side logo watermark.
	if (props.row) {
		const has_balance = balance_token != null && balance_token !== ""

		return (
			<div style={{
				position: "relative",
				zIndex: 0,
				overflow: "hidden",
				background: coinProps.background_css || "",
				borderRadius: "0.5em",
				padding: "1.5em 1.2em",
				color: appConfig.theme.textColor1,
			}}>
				{coinProps.logo_banner ?
					<img src={coinProps.logo_banner} alt={coinProps.ticker + " logo"}
						style={{
							top: "50%",
							right: "0%",
							transform: "translateY(-50%)",
							opacity: 0.13,
							zIndex: -1,
							overflow: "hidden",
							position: "absolute",
							borderRadius: "12px",
							height: "180%",
						}} />
					: null}

				<div style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "0.8em",
					position: "relative",
					zIndex: 1,
				}}>
					<div style={{
						display: "flex", alignItems: "center",
						gap: "0.7em", minWidth: 0,
					}}>
						{coinProps.logo ?
							<img src={coinProps.logo} alt={coinProps.ticker + " logo"}
								style={{
									height: "40px", width: "40px",
									objectFit: "contain", flexShrink: 0,
								}} />
							: null}
						<span style={{
							fontWeight: 700,
							fontSize: "1.25em",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
							{coinProps.name}
						</span>
					</div>

				<div style={{
					display: "flex",
					alignItems: "baseline",
					gap: "0.4em",
					color: accent,
					fontWeight: 700,
					whiteSpace: "nowrap",
					textShadow: "0 1px 3px rgba(0,0,0,0.4)",
				}}>
						<span style={{ fontSize: "1.8em", letterSpacing: "0.5px", lineHeight: 1 }}>
							{has_balance ?
								<FormattedNumber value={balance_token} decimals="2" separator=" " />
								: <Skeleton variant="text"
									width="2.5em"
									height="1em"
									sx={{
										display: "inline-block",
										bgcolor: "rgba(255,255,255,0.12)",
									}} />}
						</span>
						<span style={{
							fontSize: "1em",
							fontWeight: 700,
							letterSpacing: "1px",
							opacity: 0.85,
							minWidth: "2.5em",
							textAlign: "left",
						}}>
							{coinProps.ticker}
						</span>
					</div>
				</div>
			</div>
		)
	}

	return (
		<TextDisplayToken tokenid={coinProps.tokenid}>
			<div style={{ padding: "0.2em 0" }}>
				{jsx_header}
				{jsx_balance}
				{jsx_address}
			</div>
		</TextDisplayToken>
	)
}
